import type {
  BaseSuggestionListProps,
  createCustomSuggestionExtension as CreateCustomSuggestionExtensionType,
  Extension,
  SuggestionListRef,
} from 'OpalChat/TiptapEditorTypes';
import { useEffect, useMemo, useRef, useState } from 'react';

type CreateCustomSuggestionExtensionFn = typeof CreateCustomSuggestionExtensionType;

/**
 * Generic hook for creating suggestion extensions with any component.
 * Uses createCustomSuggestionExtension from chat-frontend via Module Federation.
 *
 * @example
 * const extensions = useSuggestionExtension({
 *   name: 'customSuggestion',
 *   char: '@',
 *   config: myConfig,
 *   component: MyCustomSuggestionList,
 * });
 */
export function useSuggestionExtension<TConfig>(options: {
  name: string;
  char: string;
  config: TConfig;
  component: React.ForwardRefExoticComponent<
    TConfig & BaseSuggestionListProps & React.RefAttributes<SuggestionListRef>
  >;
}): Extension[] | null {
  const { name, char, config, component } = options;
  const [createFn, setCreateFn] = useState<CreateCustomSuggestionExtensionFn | null>(null);
  const configRef = useRef<TConfig>(config);

  // Keep ref updated with latest config
  useEffect(() => {
    configRef.current = config;
  }, [config]);

  useEffect(() => {
    let mounted = true;

    import('OpalChat/TiptapEditorTypes')
      .then(mod => {
        if (mounted) {
          setCreateFn(() => mod.createCustomSuggestionExtension);
        }
      })
      .catch(error => {
        console.error('Failed to load TiptapEditorTypes:', error);
      });

    return () => {
      mounted = false;
    };
  }, []);

  const extensions = useMemo(() => {
    if (!createFn) {
      return null;
    }
    return [
      createFn({
        name,
        char,
        configRef,
        component,
      }),
    ];
  }, [createFn, name, char, component]);

  return extensions;
}
